<template>
  <span class="cf-value">
    <!-- password -->
    <template v-if="fieldType === 'password'">
      <span class="cf-password">{{ showPassword ? displayValue : '••••••••' }}</span>
      <ion-button fill="clear" size="small" class="cf-eye-btn" @click.stop="togglePassword">
        <ion-icon slot="icon-only" :icon="showPassword ? eyeOffOutline : eyeOutline" />
      </ion-button>
    </template>

    <!-- boolean -->
    <ion-icon v-else-if="fieldType === 'boolean'" :icon="value ? checkmarkOutline : closeOutline" />

    <!-- url -->
    <a v-else-if="fieldType === 'url'" :href="String(value)" target="_blank" rel="noopener">{{ value }}</a>

    <!-- email -->
    <a v-else-if="fieldType === 'email'" :href="`mailto:${value}`">{{ value }}</a>

    <!-- datetime -->
    <span v-else-if="fieldType === 'datetime'">{{ formatDateTime(value) }}</span>

    <!-- textarea -->
    <span v-else-if="fieldType === 'textarea'" class="cf-textarea-value">{{ value }}</span>

    <!-- currency -->
    <span v-else-if="fieldType === 'currency'">{{ formatCurrency(value) }}</span>

    <!-- object_ref -->
    <router-link v-else-if="fieldType === 'object_ref' && objectName" :to="`/item/${value}/detail`">
      {{ objectName }}
    </router-link>
    <span v-else-if="fieldType === 'object_ref'" class="cf-ref-missing">{{ value }}</span>

    <!-- image_ref -->
    <ApiImage v-else-if="fieldType === 'image_ref' && value" :uuid="String(value)" :size="[64, 64]" />

    <!-- user_ref -->
    <span v-else-if="fieldType === 'user_ref'">User #{{ value }}</span>

    <!-- barcode_ref -->
    <span v-else-if="fieldType === 'barcode_ref'">{{ barcodeCode }}</span>

    <!-- fallback: text, integer, decimal, email, etc. -->
    <span v-else>{{ value }}</span>
  </span>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { IonButton, IonIcon } from '@ionic/vue';
import { checkmarkOutline, closeOutline, eyeOutline, eyeOffOutline } from 'ionicons/icons';
import { RouterLink } from 'vue-router';
import ApiImage from '@/components/ApiImage.vue';
import { useApiStore } from '@/stores/api';
import type { CustomFieldType, PasswordMode, TypeConfig } from '@/models/custom-fields';
import { displayValue as displayDateTime, resolveDateTimeConfig } from '@/helper/datetime';
import { DEFAULT_CURRENCY, formatAmount } from '@/helper/currency';
import { unlockOf } from '@/helper/passwordCrypto';
import { askAndDecryptPassword } from '@/composables/usePasswordUnlock';

const props = defineProps<{
  fieldType: CustomFieldType;
  passwordMode?: PasswordMode | null;
  typeConfig?: TypeConfig | null;
  value: any;
}>();

const api = useApiStore();
const showPassword = ref(false);

/** The decrypted password while it is shown, dropped again when it is hidden. */
const revealed = ref<string | null>(null);

async function togglePassword() {
  if (showPassword.value) {
    showPassword.value = false;
    revealed.value = null;
    return;
  }
  if (unlockOf(props.value) !== null) {
    const plain = await askAndDecryptPassword(props.value);
    if (plain === null) return;
    revealed.value = plain;
  }
  showPassword.value = true;
}

// An encrypted value is never shown as stored, a password typed before the encryption existed is still text.
const displayValue = computed(() => (unlockOf(props.value) !== null ? revealed.value ?? '' : props.value));

const objectName = computed<string | null>(() => {
  if (props.fieldType !== 'object_ref' || !props.value) return null;
  return api.byId(Number(props.value))?.data?.object?.name ?? null;
});

const barcodeCode = computed<string>(() => {
  if (props.fieldType !== 'barcode_ref' || !props.value) return String(props.value ?? '');
  for (const obj of api.objects) {
    const bc = obj.barcodes.find((b) => b.object__id === Number(props.value));
    if (bc) return bc.code;
  }
  return `#${props.value}`;
});

function formatDateTime(v: any): string {
  if (!v) return '';
  return displayDateTime(v, resolveDateTimeConfig(props.typeConfig));
}

function formatCurrency(v: any): string {
  if (!v || typeof v !== 'object') return String(v ?? '');
  try {
    return formatAmount(v.amount ?? 0, v.currency ?? DEFAULT_CURRENCY);
  } catch {
    return `${v.amount} ${v.currency}`;
  }
}
</script>

<style scoped>
.cf-value {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.cf-eye-btn {
  --padding-start: 4px;
  --padding-end: 4px;
  height: 24px;
}
.cf-ref-missing {
  color: var(--ion-color-medium);
}
.cf-textarea-value {
  white-space: pre-wrap;
}
</style>
